import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  makeStyles,
  List,
  ListItem,
  ListItemText,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Alert } from "@material-ui/lab";
import { FileCopy } from "@material-ui/icons";
import { useTranslation } from "../hooks/useTranslation";
import { getApiBaseUrl } from "../config";
import {
  getRuleIconComponent,
  SecurityIcon,
  CategoryIcon,
  buildServicesIconCatalog,
  resolveRuleIconName,
} from "../utils/ruleIcons";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    backgroundColor: "var(--ikaro-orange-primary)",
    color: "white",
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(3),
    maxHeight: "80vh",
    overflowY: "auto",
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  reviewBlock: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: "var(--ikaro-gray-light)",
    borderRadius: 8,
  },
  treeWrap: {
    marginTop: theme.spacing(2),
    border: "1px solid var(--ikaro-gray-medium)",
    borderRadius: 8,
    padding: theme.spacing(1),
    maxHeight: "min(60vh, 520px)",
    overflowY: "auto",
    backgroundColor: "var(--ikaro-bg-surface, #fff)",
  },
  expansionPanel: {
    marginBottom: theme.spacing(1),
    "&:before": { display: "none" },
    boxShadow: "none",
    border: "1px solid var(--ikaro-gray-medium)",
    borderRadius: "8px !important",
    overflow: "hidden",
  },
  expansionSummary: {
    minHeight: 48,
    "&.Mui-expanded": { minHeight: 48 },
  },
  expansionSummaryContent: {
    margin: "12px 0",
    "&.Mui-expanded": { margin: "12px 0" },
  },
  ruleItem: {
    borderRadius: 8,
    marginBottom: theme.spacing(0.75),
    backgroundColor: "var(--ikaro-gray-light)",
    alignItems: "flex-start",
    display: "flex",
  },
  ruleIconWrap: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
    marginTop: 4,
    flexShrink: 0,
  },
  ruleIconService: {
    backgroundColor: "rgba(255, 152, 0, 0.15)",
    color: "var(--ikaro-orange-primary, #f57c00)",
  },
  ruleIconProduct: {
    backgroundColor: "rgba(76, 175, 80, 0.15)",
    color: "#4caf50",
  },
  productBlock: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid var(--ikaro-gray-medium)",
  },
  orphanPanel: {
    marginTop: theme.spacing(2),
  },
}));

function countProductRules(products) {
  if (!products || !products.length) return 0;
  return products.reduce(
    (acc, p) => acc + (p.rules && p.rules.length ? p.rules.length : 0),
    0,
  );
}

const CopyUserModal = ({ open, onClose, sourceUser, token, onUserCreated }) => {
  const classes = useStyles();
  const { t } = useTranslation("jobs");
  const API_BASE_URL = getApiBaseUrl();

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [success, setSuccess] = useState(false);
  /** Mesma fonte que UserManagement → ServiceTree: GET /users/services */
  const [ruleIconCatalog, setRuleIconCatalog] = useState(null);

  useEffect(() => {
    if (!open || !sourceUser?.id) return;
    setStep(0);
    setName("");
    setEmail("");
    setPassword("");
    setPreview(null);
    setPreviewError(null);
    setSubmitError(null);
    setSuccess(false);
    setLoadingPreview(true);
    fetch(`${API_BASE_URL}/users/copy_user_preview/${sourceUser.id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || data.message || `Erro ${res.status}`);
        }
        return data;
      })
      .then(setPreview)
      .catch((err) => setPreviewError(err.message || "Erro ao carregar preview"))
      .finally(() => setLoadingPreview(false));
  }, [open, sourceUser?.id, token, API_BASE_URL]);

  useEffect(() => {
    if (step !== 1 || !preview?.tenant_id || !token || !sourceUser?.id) {
      setRuleIconCatalog(null);
      return undefined;
    }
    let cancelled = false;
    const url = `${API_BASE_URL}/users/services?tenant_id=${preview.tenant_id}&for_user_id=${sourceUser.id}`;
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data) => {
        if (cancelled) return;
        setRuleIconCatalog(buildServicesIconCatalog(data.services || []));
      })
      .catch(() => {
        if (!cancelled) setRuleIconCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, [step, preview?.tenant_id, sourceUser?.id, token, API_BASE_URL]);

  const hasCopyableRules =
    preview &&
    ((preview.service_rules && preview.service_rules.length > 0) ||
      (preview.product_rules && preview.product_rules.length > 0));

  const serviceGroups = preview?.service_groups || [];

  const handleConfirmCopy = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = {
        source_user_id: sourceUser.id,
        name: name.trim(),
        email: email.trim(),
      };
      if (password) body.password = password;
      const res = await fetch(`${API_BASE_URL}/users/copy_user_from`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.message || `Erro ${res.status}`);
      }
      setSuccess(true);
      setTimeout(() => {
        onUserCreated();
        onClose();
      }, 1200);
    } catch (e) {
      setSubmitError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canGoReview = name.trim() && email.trim() && hasCopyableRules && !loadingPreview && !previewError;

  const renderRuleRow = (key, rule, variant, productCode) => {
    const iconName = resolveRuleIconName(rule, variant, productCode, ruleIconCatalog);
    const ruleForIcon = { ...rule, rule_icon: iconName || rule.rule_icon };
    const SpecificIcon = getRuleIconComponent(ruleForIcon);
    const IconComponent = SpecificIcon || (variant === "product" ? CategoryIcon : SecurityIcon);
    const primary =
      rule.rule_description ||
      rule.rule_name ||
      rule.rule_code ||
      rule.service_rule_id ||
      rule.rule_id;
    let secondary = null;
    if (rule.rule_code && rule.rule_code !== primary) secondary = rule.rule_code;
    else if (rule.rule_name && rule.rule_name !== primary) secondary = rule.rule_name;
    if (rule.product_name || rule.product_code) {
      const pl = rule.product_name || rule.product_code;
      if (pl && pl !== primary) {
        secondary = secondary ? `${pl} · ${secondary}` : pl;
      }
    }
    return (
      <ListItem key={key} className={classes.ruleItem} alignItems="flex-start">
        <Box
          className={`${classes.ruleIconWrap} ${
            variant === "product" ? classes.ruleIconProduct : classes.ruleIconService
          }`}
        >
          <IconComponent fontSize="small" />
        </Box>
        <ListItemText
          primary={primary}
          secondary={secondary}
          primaryTypographyProps={{ variant: "body1" }}
          secondaryTypographyProps={{ variant: "caption", color: "textSecondary" }}
        />
      </ListItem>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <FileCopy className="mr-8" />
          {t("actions.copyUser")}
          {sourceUser?.name ? ` — ${sourceUser.name}` : ""}
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loadingPreview && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {previewError && (
          <Alert severity="error" className="mb-16">
            {previewError}
          </Alert>
        )}

        {success && (
          <Alert severity="success">{t("actions.copyUserSuccess")}</Alert>
        )}

        {!loadingPreview && !success && (
          <>
            {step === 0 && (
              <>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {t("actions.copyUserStep1Hint")}
                </Typography>
                <TextField
                  fullWidth
                  label={t("table.name")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className={classes.formField}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label={t("table.email")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={classes.formField}
                  variant="outlined"
                />
                <TextField
                  fullWidth
                  label={t("table.password")}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  helperText={t("actions.copyUserPasswordOptional")}
                  className={classes.formField}
                  variant="outlined"
                />
                {!hasCopyableRules && preview && !previewError && (
                  <Alert severity="warning">{t("actions.copyUserNoRules")}</Alert>
                )}
              </>
            )}

            {step === 1 && preview && (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  {t("actions.copyUserReviewTitle")}
                </Typography>
                <Box className={classes.reviewBlock}>
                  <Typography variant="body2">
                    <strong>{t("table.company")}:</strong> {preview.company_name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>{t("table.tenant")}:</strong> {preview.tenant_name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t("actions.copyUserModel")}: {preview.source_user?.name} ({preview.source_user?.email})
                  </Typography>
                </Box>
                <Alert severity="info" style={{ marginTop: 16 }}>
                  {preview.note || t("actions.copyUserCellsNote")}
                </Alert>
                <Typography variant="subtitle2" style={{ marginTop: 16 }}>
                  {t("actions.copyUserTreeHeading")}
                </Typography>
                <Box className={classes.treeWrap}>
                  {serviceGroups.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      {t("actions.copyUserNoServiceTree")}
                    </Typography>
                  ) : (
                    serviceGroups.map((group, idx) => {
                      const nRules = (group.rules || []).length;
                      const nProdRules = countProductRules(group.products);
                      const nTotal = nRules + nProdRules;
                      return (
                        <ExpansionPanel
                          key={group.service_id}
                          className={classes.expansionPanel}
                          defaultExpanded={idx === 0}
                        >
                          <ExpansionPanelSummary
                            expandIcon={<ExpandMoreIcon />}
                            className={classes.expansionSummary}
                            classes={{ content: classes.expansionSummaryContent }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              width="100%"
                              pr={1}
                            >
                              <Typography variant="subtitle1" component="span">
                                {group.service_description}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                component="span"
                                style={{ flexShrink: 0, marginLeft: 8 }}
                              >
                                {nTotal}{" "}
                                {nTotal === 1
                                  ? t("actions.copyUserPermissionCountOne")
                                  : t("actions.copyUserPermissionCountMany")}
                              </Typography>
                            </Box>
                          </ExpansionPanelSummary>
                          <ExpansionPanelDetails style={{ display: "block" }}>
                            {nRules > 0 && (
                              <>
                                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                                  {t("actions.copyUserServiceRulesHeading")}
                                </Typography>
                                <List dense disablePadding>
                                  {(group.rules || []).map((rule) =>
                                    renderRuleRow(rule.service_rule_id, rule, "service", null),
                                  )}
                                </List>
                              </>
                            )}

                            {(group.products || []).map((product) => (
                              <Box
                                key={product.product_code}
                                className={classes.productBlock}
                              >
                                <Typography variant="subtitle2" gutterBottom>
                                  {t("serviceTree.products")}: {product.product_name || product.product_code}
                                </Typography>
                                <List dense disablePadding>
                                  {(product.rules || []).map((pr) =>
                                    renderRuleRow(
                                      `${product.product_code}-${pr.rule_id}`,
                                      pr,
                                      "product",
                                      product.product_code,
                                    ),
                                  )}
                                </List>
                              </Box>
                            ))}
                          </ExpansionPanelDetails>
                        </ExpansionPanel>
                      );
                    })
                  )}
                </Box>

                {(preview.product_rules_orphans || []).length > 0 && (
                  <ExpansionPanel
                    className={`${classes.expansionPanel} ${classes.orphanPanel}`}
                    defaultExpanded
                  >
                    <ExpansionPanelSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.expansionSummary}
                      classes={{ content: classes.expansionSummaryContent }}
                    >
                      <Typography variant="subtitle1">
                        {t("actions.copyUserOrphanProducts")}
                      </Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails style={{ display: "block" }}>
                      <List dense disablePadding>
                        {(preview.product_rules_orphans || []).map((r) =>
                          renderRuleRow(
                            `orphan-${r.product_code}-${r.rule_id}`,
                            r,
                            "product",
                            r.product_code,
                          ),
                        )}
                      </List>
                    </ExpansionPanelDetails>
                  </ExpansionPanel>
                )}

                <Typography variant="body2" style={{ marginTop: 16 }}>
                  <strong>{t("table.name")}:</strong> {name.trim()}
                </Typography>
                <Typography variant="body2">
                  <strong>{t("table.email")}:</strong> {email.trim()}
                </Typography>
              </>
            )}

            {submitError && (
              <Alert severity="error" style={{ marginTop: 16 }}>
                {submitError}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={submitting}>
          {t("actions.cancel")}
        </Button>
        {!loadingPreview && !success && step === 0 && (
          <Button
            color="primary"
            variant="contained"
            disabled={!canGoReview}
            onClick={() => setStep(1)}
          >
            {t("actions.copyUserReview")}
          </Button>
        )}
        {!loadingPreview && !success && step === 1 && (
          <>
            <Button color="primary" variant="outlined" onClick={() => setStep(0)} disabled={submitting}>
              {t("actions.previous")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={submitting || !hasCopyableRules}
              onClick={handleConfirmCopy}
            >
              {submitting ? t("actions.creating") : t("actions.copyUserConfirm")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CopyUserModal;
